<!-- The loader (DESIGN.md "Hi-Vis" → Loading): straight down on a donut of
     shaggy couch, the Redgum 46 mows a ring round it, clockwise. Behind it the
     ring is fresh stripe; ahead it's still long, and the oldest of the cut
     grows back into the long as it comes round again, so the loop has no
     seam. The side chute throws a puff of clippings inward; they land, stay
     put while the mower drives on, and fade.

     Pictures, not WebGL: three layers rendered from the game's own GLB and
     turf colours (tools/blender/mow_loader.py → public/assets/loader, ~30 KB,
     never precached), turned by CSS transforms and opacity only, so it runs
     on the compositor before three.js has loaded, and costs nothing when it
     does. Until the pictures land, a soft donut holds the space.

     The rig turns once per lap; the cut ring's conic mask turns with it, and
     the stripe inside counter-turns so the grass stays still under it. Each
     clipping counter-turns the same way once thrown, which pins it to the
     ground. Reduced motion (the OS or Settings): a still frame, the fresh cut
     breathing slowly.

     `size`: 'small' (64 px, inline), 'large' (160 px, panels), or any CSS
     length. The default slot is the caption; the art is decorative and the
     root is a status with `label` for assistive tech. -->
<template>
  <div class="hv-mow" :class="[`is-${layout}`]" :style="{ '--ml-s': px }" role="status" :aria-label="label">
    <div class="ml-disc" aria-hidden="true">
      <img class="ml-patch" :class="{ in: seen.patch }" :src="art.patch" alt="" decoding="async" loading="lazy" draggable="false" @load="seen.patch = true" />
      <div class="ml-trail">
        <img class="ml-cut" :class="{ in: seen.cut }" :src="art.cut" alt="" decoding="async" loading="lazy" draggable="false" @load="seen.cut = true" />
      </div>
      <div class="ml-rig">
        <span v-for="b in BITS" :key="b.i" class="ml-bit" :style="b.style">
          <img :src="art.clip" alt="" decoding="async" loading="lazy" draggable="false" />
        </span>
        <span class="ml-mower">
          <img :class="{ in: seen.mower }" :src="art.mower" alt="" decoding="async" loading="lazy" draggable="false" @load="seen.mower = true" />
        </span>
      </div>
    </div>
    <span v-if="$slots.default" class="ml-cap"><slot /></span>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue';

const props = withDefaults(defineProps<{
  /** 'small' ≈ 64 px (inline), 'large' ≈ 160 px (panels), or a CSS length (e.g. 'min(160px, 40%)'). */
  size?: 'small' | 'large' | number | string;
  /** What assistive tech hears. */
  label?: string;
}>(), { size: 'large', label: 'Loading' });

const px = computed(() => {
  const s = props.size;
  if (s === 'small') return '64px';
  if (s === 'large') return '160px';
  return typeof s === 'number' ? `${s}px` : s;
});
/** Small sits in a line with its caption; anything else stacks. */
const layout = computed(() => (props.size === 'small' ? 'row' : 'stack'));

const base = (import.meta.env.BASE_URL ?? '/').replace(/\/?$/, '/');
const art = {
  patch: `${base}assets/loader/patch.webp`,
  cut: `${base}assets/loader/cut.webp`,
  mower: `${base}assets/loader/mower.webp`,
  clip: `${base}assets/loader/clip.svg`,
};
const seen = reactive({ patch: false, cut: false, mower: false });

/**
 * The clippings: one thrown every 1/N of a lap, each with its own throw
 * (inward and a little back, in % of its own size) and spin. Fixed, not random,
 * so every loader and every lap looks the same.
 */
const N = 10;
const BITS = Array.from({ length: N }, (_, i) => {
  const r = [0.2, 0.75, 0.45, 0.95, 0.05, 0.6, 0.35, 0.85, 0.15, 0.5][i];
  return {
    i,
    style: {
      animationDelay: `calc(var(--ml-t) * ${-(i / N).toFixed(3)})`,
      '--bx': `${Math.round(-15 - r * 45)}%`,
      '--by': `${Math.round(45 + ((r * 7) % 1) * 45)}%`,
      '--br': `${Math.round(40 + r * 260)}deg`,
      '--bs': (0.7 + ((r * 3) % 1) * 0.5).toFixed(2),
    },
  };
});
</script>

<style scoped>
/*
 * Geometry, as fractions of the disc (tools/blender/mow_loader.py, patch radius 1):
 * the mower's sprite square is 0.33 of the disc and its centre rides the ring at
 * 0.30 from the middle; the blade is 0.052 ahead of the sprite's centre (~10°), so
 * the cut's hard edge sits at 6°, under the deck. The chute is at the sprite's right:
 * at the top of the lap, 0.05 ahead and 0.10 in from the ring.
 */
.hv-mow {
  --ml-t: 2s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--hv-mute);
}
.hv-mow.is-stack { flex-direction: column; gap: 12px; }
.ml-disc {
  position: relative;
  flex: none;
  width: var(--ml-s);
  height: var(--ml-s);
  border-radius: 50%;
  /* The donut until the lawn lands: the glass's sunk tone, the hole cut out. */
  background: radial-gradient(circle closest-side, transparent 0 23%, var(--hv-sunk) 24% 99%, transparent 100%);
  contain: layout paint;
}
.ml-disc img {
  display: block;
  user-select: none;
  -webkit-user-select: none;
  -webkit-user-drag: none;
  pointer-events: none;
}
.ml-patch, .ml-cut {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity 240ms ease-out;
}
.ml-patch.in, .ml-cut.in, .ml-mower img.in { opacity: 1; }

/* The cut ring: shown behind the mower, growing back ahead of it. */
.ml-trail, .ml-rig {
  position: absolute;
  inset: 0;
  will-change: transform;
}
.ml-trail {
  -webkit-mask-image: conic-gradient(from 6deg, transparent 0deg, transparent 50deg, black 140deg, black 359.5deg, transparent 360deg);
  mask-image: conic-gradient(from 6deg, transparent 0deg, transparent 50deg, black 140deg, black 359.5deg, transparent 360deg);
  animation: ml-lap var(--ml-t) linear infinite;
}
.ml-cut { animation: ml-unlap var(--ml-t) linear infinite; will-change: transform; }
.ml-rig { animation: ml-lap var(--ml-t) linear infinite; }

/* The mower: centred on the ring at the top of the lap, nose clockwise. */
.ml-mower {
  position: absolute;
  left: 33.5%;
  top: 3.5%;
  width: 33%;
  height: 33%;
  transform: rotate(90deg);
}
.ml-mower img {
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity 240ms ease-out;
  animation: ml-idle 0.1s ease-in-out infinite alternate;
}

/* A clipping: thrown from the chute, landed, pinned to the ground (the counter-turn), faded. */
.ml-bit {
  position: absolute;
  inset: 0;
  animation: ml-pin var(--ml-t) linear infinite;
}
.ml-bit img {
  position: absolute;
  left: 54%;
  top: 29%;
  width: 9%;
  height: 9%;
  margin: -4.5% 0 0 -4.5%;
  opacity: 0;
  animation: ml-throw var(--ml-t) linear infinite;
  animation-delay: inherit;
}

@keyframes ml-lap { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes ml-unlap { from { transform: rotate(0deg); } to { transform: rotate(-360deg); } }
@keyframes ml-pin { from { transform: rotate(0deg); } to { transform: rotate(-360deg); } }
@keyframes ml-idle {
  from { transform: translate(0.6%, 0) rotate(-0.8deg); }
  to { transform: translate(-0.6%, 0.4%) rotate(0.8deg); }
}
@keyframes ml-throw {
  0% { opacity: 0; transform: translate(0, 0) rotate(0deg) scale(0.4); animation-timing-function: cubic-bezier(0.2, 0.7, 0.3, 1); }
  3% { opacity: 1; }
  14% { opacity: 1; transform: translate(var(--bx), var(--by)) rotate(var(--br)) scale(var(--bs)); }
  30% { opacity: 0.85; transform: translate(var(--bx), var(--by)) rotate(var(--br)) scale(var(--bs)); }
  42%, 100% { opacity: 0; transform: translate(var(--bx), var(--by)) rotate(var(--br)) scale(var(--bs)); }
}

.ml-cap {
  font-family: var(--hv-font);
  font-size: var(--hv-fs-sm);
  font-weight: 600;
  letter-spacing: 0.01em;
  color: var(--hv-mute);
  text-align: center;
}
.hv-mow.is-row .ml-cap { text-align: start; }

/* Reduced motion from the OS: a still frame, the mower at the top of its lap with the cut behind it, the fresh cut breathing slowly. */
@media (prefers-reduced-motion: reduce) {
  .ml-rig, .ml-cut, .ml-mower img, .ml-bit, .ml-bit img { animation: none; }
  .ml-trail { animation: ml-breathe 2.4s ease-in-out infinite alternate; }
}
@keyframes ml-breathe { from { opacity: 1; } to { opacity: 0.55; } }
</style>

<style>
/* Settings → Reduced motion zeroes every animation (app.css, !important), which
   would leave each layer wherever its clock stood: the same still frame instead. */
html.ns-reduced-motion .hv-mow .ml-rig,
html.ns-reduced-motion .hv-mow .ml-cut,
html.ns-reduced-motion .hv-mow .ml-mower img,
html.ns-reduced-motion .hv-mow .ml-bit,
html.ns-reduced-motion .hv-mow .ml-bit img { animation: none !important; }
html.ns-reduced-motion .hv-mow .ml-trail { animation: hv-mow-breathe 2.4s ease-in-out infinite alternate !important; }
@keyframes hv-mow-breathe { from { opacity: 1; } to { opacity: 0.55; } }
</style>
